import { useEffect } from 'react'
import { useHistory } from 'react-router-dom' // todo, needs alternate for migration to v6
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom-v5-compat'

import Bugsnag from '@bugsnag/browser'
import type { History } from 'history'
import { replace } from 'redux-first-history'

import { useSplitEnabled } from '@buffer-mono/features'

import {
  IdeaCreationDialog,
  IdeaDetailsDialog,
  IdeaEditDialog,
} from '~publish/components/IdeaManagementRouter'
import {
  PostCreationDialog,
  PostDetailsDialog,
  PostEditDialog,
} from '~publish/components/PostDialogs'
import {
  usePageLoad,
  useCurrentOrganization,
  useIsOneBufferOrg,
} from '~publish/contexts/AccountContext'
import { useConnectChannelModal } from '~publish/hooks/useConnectChannelModal'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useStreaksAutoRefresh } from '~publish/hooks/useStreaksAutoRefresh'
import { useSyncUnrepliedCommentsCount } from '~publish/hooks/useSyncUnrepliedCommentsCount'
import CampaignForm from '~publish/legacy/campaign-form'
import ListCampaigns from '~publish/legacy/campaigns-list'
import { CALENDAR_PAGE } from '~publish/legacy/constants'
import { useAppDispatch } from '~publish/legacy/store'
import { setLastVisitedPage } from '~publish/legacy/utils/page-visits'
import { AllChannelsPage } from '~publish/pages/AllChannels'
import CalendarPage from '~publish/pages/Calendar/CalendarPage'
import { ChannelPage } from '~publish/pages/Channel'
import { ChannelSettings } from '~publish/pages/Channel/ChannelSettings'
import { ChannelShopGrid } from '~publish/pages/Channel/ChannelShopGrid'
import { CommentsPage } from '~publish/pages/Comments'
import { CommentsChannel } from '~publish/pages/Comments/CommentsChannel'
import CreatePage from '~publish/pages/Create'
import { CollectionList as FeedCollectionList } from '~publish/pages/Create/Feed/components/CollectionList/CollectionList'
import { FeedList } from '~publish/pages/Create/Feed/components/FeedList/FeedList'
import { IdeasPage } from '~publish/pages/Create/IdeasPage'
import { TemplateDialogRoute } from '~publish/pages/Create/Templates/components/Template/TemplateDialogRoute'
import { TemplateLibraryPage } from '~publish/pages/Create/Templates/components/TemplateLibraryPage/TemplateLibraryPage'
import { NewPostPage } from '~publish/pages/NewPost'
import AppsExtrasPage from '~publish/pages/Settings/AppsExtrasPage'
import BetaFeatures from '~publish/pages/Settings/BetaFeatures'
import { Billing } from '~publish/pages/Settings/Billing'
import { ChannelGroups as SettingsChannelGroups } from '~publish/pages/Settings/ChannelGroups/ChannelGroups'
import Channels from '~publish/pages/Settings/Channels'
import EmailNotifications from '~publish/pages/Settings/EmailNotifications'
import GeneralSettings from '~publish/pages/Settings/General'
import { useCanAccessTeamFeatures } from '~publish/pages/Settings/hooks/useCanAccessTeamFeatures'
import Preferences from '~publish/pages/Settings/Preferences'
import ProfileSettings from '~publish/pages/Settings/ProfileSettings'
import ReferAFriend from '~publish/pages/Settings/ReferAFriend'
import { SavedRepliesSettings } from '~publish/pages/Settings/SavedRepliesSettings'
import Settings from '~publish/pages/Settings/Settings'
import Tags from '~publish/pages/Settings/Tags'
import Team from '~publish/pages/Settings/Team'

import ComposePage from '../../../compose-page'
import { CommentsRouteHandler } from '../CommentsRouteHandler'
import { PostDeepLinkResolver } from '../PostDeepLinkResolver/PostDeepLinkResolver'

declare global {
  // window.__history is used to share the history object with the app shell to have smooth navigations
  interface Window {
    __history: History<unknown> | undefined
    __isComposerOpen?: boolean
  }
}

const CalendarRedirect = ({
  view,
}: {
  view: 'month' | 'week'
}): JSX.Element => {
  return (
    <Navigate
      to={{
        ...location,
        pathname: `/all-channels/calendar/${view}`,
      }}
    />
  )
}

const CatchAllRedirect = (): JSX.Element => {
  const location = useLocation()

  if (location.pathname !== '/') {
    // eslint-disable-next-line no-console
    console.log('Catch-all route triggered for path: ' + location.pathname)
    Bugsnag.notify(
      new Error('Catch-all route triggered for path: ' + location.pathname),
      (event) => {
        event.addMetadata('location', {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        })
      },
    )
  }

  return <Navigate to="/all-channels" replace />
}

export const AppPages = (): JSX.Element => {
  usePageTitle('Publish')

  // Get current selected org from account
  const { createNewPostInComposer, isOpen: isComposerOpen } = usePostComposer()
  const dispatch = useAppDispatch()

  const history = useHistory()
  useEffect(() => {
    // Make history object available globally so that it can be used in the app shell
    window.__history = history
  }, [history])

  useEffect(() => {
    // Make post composer and idea composer available globally so that it can be used in the app shell
    window.__openComposer = createNewPostInComposer
    window.__isComposerOpen = isComposerOpen
  }, [createNewPostInComposer, isComposerOpen])

  const { updateInitialPageLoad } = usePageLoad()

  const params = useParams()
  const TagViewRedirect = (): JSX.Element => {
    return (
      <Navigate
        replace
        to={`/all-channels${params.id ? `?tags[]=${params.id}` : ''}${
          params.tab ? `&tab=${params.tab}` : ''
        }`}
      />
    )
  }

  useEffect(() => {
    updateInitialPageLoad()
  })

  /**
   * IMPORTANT NOTE: avoid to place here any hook that contains state vars
   * as it will re-render the entire router.
   */
  useConnectChannelModal()
  useStreaksAutoRefresh()
  useSyncUnrepliedCommentsCount()

  const location = useLocation()
  const [searchParams] = useSearchParams()
  // eslint-disable-next-line
  const channelSelectedThroughRoute = (location.state as { channel?: string })
    ?.channel

  const shouldPreOpenChannelStorefront =
    searchParams.get('channelStorefront') === 'true'

  useEffect(() => {
    // Reset channel to select state included in some location changes
    if (channelSelectedThroughRoute) {
      dispatch(replace(location.pathname, {}))
    }

    /* TODO: we could include here others setLastVisitedPage calls, as the one for queue, for example */
    if (location.pathname.includes('calendar')) {
      setLastVisitedPage(CALENDAR_PAGE)
    }
  }, [location, channelSelectedThroughRoute, dispatch])

  const currentOrganization = useCurrentOrganization()
  const isOneBufferOrganization = useIsOneBufferOrg()
  const showBetaFeatures = Boolean(
    !currentOrganization || isOneBufferOrganization,
  )
  const { canAccessTeamFeatures, isLoading: isLoadingTeamFeatures } =
    useCanAccessTeamFeatures()

  const loadTeamRoute =
    isLoadingTeamFeatures || (canAccessTeamFeatures && !isLoadingTeamFeatures)

  const { isEnabled: isTagsEnabled, isReady: isTagsReady } = useSplitEnabled(
    'tags-management-on-settings',
    {},
    'on', // We need to set the enabled treatment to on to avoid the default treatment being used while the feature flag is being loaded and causing the redirect to the settings page
    'on',
  )

  const loadTagsRoute = !isTagsReady || !currentOrganization || isTagsEnabled

  return (
    <Routes>
      <Route path={'/compose'} element={<ComposePage />} />

      {/* Calendar */}
      <Route
        path="/all-channels/calendar/:granularity/*"
        element={<CalendarPage />}
      >
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
      </Route>
      <Route
        path="/channels/:id/calendar/:granularity/*"
        element={<CalendarPage />}
      >
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
      </Route>
      <Route
        path="/calendar/month"
        element={<CalendarRedirect view="month" />}
      />
      <Route path="/calendar/week" element={<CalendarRedirect view="week" />} />

      {/* All Channels */}
      <Route path="/all-channels/*" element={<AllChannelsPage />}>
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
      </Route>

      {/* Channel */}
      <Route path="/channels/:id/settings" element={<ChannelSettings />} />
      <Route path="/channels/:id/shop-grid" element={<ChannelShopGrid />} />
      <Route path="/channels/:id/*" element={<ChannelPage />}>
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
      </Route>

      {/* Tags (formerly Campaigns) */}
      <Route path="/campaigns/new" element={<CampaignForm />} />
      <Route path="/campaigns/:id/edit" element={<CampaignForm editMode />} />
      <Route path="/campaigns/:id/:tab" element={<TagViewRedirect />} />
      <Route path="/campaigns" element={<ListCampaigns />} />
      <Route path="/tags/new" element={<CampaignForm />} />
      <Route path="/tags/:id/edit" element={<CampaignForm editMode />} />
      <Route path="/tags/:id/:tab" element={<TagViewRedirect />} />
      <Route path={'/tags'} element={<ListCampaigns />} />

      {/* Channel Groups Redirect */}
      <Route
        path="/channel-groups"
        element={<Navigate to="/settings/channel-groups" replace />}
      />

      {/* Comments */}
      <Route path="/comments/*" element={<CommentsRouteHandler />}>
        <Route index element={<CommentsPage />} />
        <Route path=":channelId" element={<CommentsChannel />}>
          <Route path="posts/new" element={<PostCreationDialog />} />
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        </Route>
        <Route path=":channelId/:view" element={<CommentsChannel />}>
          <Route path="posts/new" element={<PostCreationDialog />} />
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        </Route>
      </Route>

      {/* Deep Linking to Posts */}
      <Route path={'/link-to-post'} element={<PostDeepLinkResolver />} />

      {/* Create */}
      <Route element={<CreatePage />}>
        {/* Feeds */}
        <Route path="/create/feeds/:feedId" element={<FeedList />}>
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route
          path="/create/feed/collections/:feedCollectionId"
          element={<FeedCollectionList />}
        >
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        {/* Templates */}
        <Route path="/create/templates/*" element={<TemplateLibraryPage />}>
          <Route path="template/:templateId" element={<TemplateDialogRoute />}>
            <Route path="ideas/new" element={<IdeaCreationDialog />} />
            <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
            <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
          </Route>
        </Route>
        {/* Ideas / Create Home */}
        <Route path="/create/*" element={<IdeasPage pageTitle="Ideas" />}>
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
      </Route>

      {/* New Post */}
      <Route path="/post/new" element={<NewPostPage />} />

      {/* Settings */}
      <Route path="/settings" element={<Settings />}>
        <Route index element={<ProfileSettings />} />
        <Route path="preferences" element={<Preferences />} />
        <Route path="notifications" element={<EmailNotifications />} />
        <Route path="apps-extras" element={<AppsExtrasPage />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route
          path="channels"
          element={
            <Channels
              preOpenChannelStorefront={shouldPreOpenChannelStorefront}
            />
          }
        />
        <Route path="billing" element={<Billing />} />
        <Route
          path="channels/:id"
          element={<ChannelSettings isForNewSettings={true} />}
        />
        <Route path="refer-a-friend" element={<ReferAFriend />} />
        <Route
          path="beta"
          element={
            showBetaFeatures ? (
              <BetaFeatures />
            ) : (
              <Navigate to="/settings" replace />
            )
          }
        />
        <Route
          path="team"
          element={
            loadTeamRoute ? <Team /> : <Navigate to="/settings" replace />
          }
        />
        <Route path="saved-replies" element={<SavedRepliesSettings />} />
        <Route
          path="tags"
          element={
            loadTagsRoute ? <Tags /> : <Navigate to="/settings" replace />
          }
        />
        <Route path="channel-groups" element={<SettingsChannelGroups />} />
        <Route path="*" element={<Navigate to="/settings" replace />} />
      </Route>

      {/* Redirects */}

      {/* Create Routes */}
      {/* Redirect old /content paths for backwards compatibility */}
      <Route
        path="/content/ideas/new" // Because of some old routing, it's possible to end up here
        element={
          <Navigate
            to={{
              ...location,
              pathname: '/create/ideas/new',
            }}
          />
        }
      />
      <Route
        path="/content/new"
        element={
          <Navigate
            to={{
              ...location,
              pathname: '/create/ideas/new',
            }}
          />
        }
      />
      <Route
        path="/content"
        element={
          <Navigate
            to={{
              ...location,
              pathname: '/create',
            }}
          />
        }
      />

      {/* Default redirect to all channels */}
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  )
}
